import { memo, UIEvent, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import palette from 'theme/palette'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { Chip, CircularProgress, IconButton, Tooltip } from '@mui/material'

import {
  COLLAPSED_ROWS,
  HIGHLIGHT_ROW,
  LOAD_MORE_SCROLL_THRESHOLD,
  ROW_TRANSLATION,
  ROW_UNIT_TRANSLATION,
  TEXT_ROWS,
  TRANSLATION_KEYS,
} from '../constants'
import { PondBlockData } from '../hooks/usePondBlocks'
import { HIGHLIGHT_BACKGROUND, tableStyles } from '../styles'
import { formatCellValue } from '../utils'

interface PondBlockProps {
  block: PondBlockData
  isMobile: boolean
  isChildPond: boolean
  hasNextPage: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
}

// One pond of the NK-281 ไซส์กุ้ง table: a header strip that toggles between the
// two headline rows (อายุกุ้ง, ไซส์กุ้ง) and all nine, over a date strip that opens
// scrolled to the newest measurement. Older dates are to the left.
export const PondBlock = memo(
  ({ block, isMobile, isChildPond, hasNextPage, isLoadingMore, onLoadMore }: PondBlockProps) => {
    const { t } = useTranslation('pondOverview')
    const [isOpen, setIsOpen] = useState(false)

    const scrollRef = useRef<HTMLDivElement>(null)
    // How far the view sits from the newest (right-hand) edge. Everything that
    // changes width — an older page, the load-more column coming or going — lands on
    // the LEFT, so holding this distance keeps the dates on screen still. It starts
    // at 0: the block opens on the newest measurement.
    const distanceFromRight = useRef(0)

    useLayoutEffect(() => {
      const el = scrollRef.current
      if (!el) return
      el.scrollLeft = el.scrollWidth - el.clientWidth - distanceFromRight.current
    }, [block.dates.length, hasNextPage])

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      distanceFromRight.current = el.scrollWidth - el.clientWidth - el.scrollLeft
      if (hasNextPage && !isLoadingMore && el.scrollLeft <= LOAD_MORE_SCROLL_THRESHOLD) {
        onLoadMore()
      }
    }

    const rows = isOpen ? block.rows : block.rows.filter((row) => COLLAPSED_ROWS.has(row.label))
    // Pond names are entered in Thai whatever the UI language, so "already carries
    // the word บ่อ" is checked against the Thai string, not the active locale's.
    const hasPondPrefix = block.pond.startsWith(t(TRANSLATION_KEYS.HEADER.POND, { lng: 'th' }))
    const pondLabel = hasPondPrefix
      ? block.pond
      : `${t(TRANSLATION_KEYS.HEADER.POND)} ${block.pond}`

    return (
      <div style={tableStyles.block(isMobile)}>
        <div
          role="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
          style={tableStyles.pondHeader(isMobile)}
        >
          <span style={tableStyles.pondName}>{pondLabel}</span>
          {isChildPond && (
            <Chip
              label={t(TRANSLATION_KEYS.BADGE.CHILD_POND)}
              size="small"
              sx={{
                height: 'auto',
                padding: '0.1em 0.3em',
                fontSize: '0.85em',
                fontWeight: 600,
                color: palette.light.info.main,
                backgroundColor: palette.light.info.lighter,
              }}
            />
          )}
          <span style={tableStyles.toggle}>
            {t(isOpen ? TRANSLATION_KEYS.HEADER.COLLAPSE : TRANSLATION_KEYS.HEADER.EXPAND)}
            <KeyboardArrowDownIcon
              sx={{
                fontSize: '1.3em',
                color: palette.light.primary.dark,
                transform: isOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </span>
        </div>

        <div ref={scrollRef} onScroll={handleScroll} style={tableStyles.scroller(isMobile)}>
          <div style={tableStyles.grid}>
            <div style={tableStyles.line}>
              <div style={tableStyles.labelHeader(isMobile)}>
                <span style={tableStyles.smallText}>{t(TRANSLATION_KEYS.HEADER.DATE)}</span>
              </div>
              {hasNextPage && (
                <div style={tableStyles.loadMoreCell(isMobile)}>
                  <Tooltip title={t(TRANSLATION_KEYS.MESSAGES.LOAD_MORE)}>
                    <span>
                      {/* Not LoadingIconButton: its spinner is a fixed 24px, which the
                          WebView's 0.5 zoom shrinks to a speck. */}
                      <IconButton
                        disabled={isLoadingMore}
                        onClick={(e) => {
                          e.stopPropagation()
                          onLoadMore()
                        }}
                        style={{ fontSize: 'inherit' }}
                      >
                        {isLoadingMore ? (
                          <CircularProgress size="1.2em" color="inherit" />
                        ) : (
                          <KeyboardBackspaceIcon sx={{ fontSize: '1.4em' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              )}
              {block.dates.map((date) => (
                <div key={date} style={tableStyles.dateHeader(isMobile)}>
                  <span style={tableStyles.smallText}>{date}</span>
                </div>
              ))}
            </div>

            {rows.map((row) => {
              const isTextRow = TEXT_ROWS.has(row.label)
              const isHighlighted = isOpen && row.label === HIGHLIGHT_ROW
              const labelKey = ROW_TRANSLATION[row.label]
              const unitKey = ROW_UNIT_TRANSLATION[row.label]

              return (
                <div key={row.label} style={tableStyles.line}>
                  <div
                    style={tableStyles.labelCell(
                      isMobile,
                      // Pre-blended over white: the pinned cell must stay opaque.
                      isHighlighted ? blendOverWhite(HIGHLIGHT_BACKGROUND) : palette.light.grey[0],
                      isTextRow
                    )}
                  >
                    <span style={tableStyles.smallText}>
                      {labelKey ? t(labelKey) : row.label}
                      {unitKey && <span style={tableStyles.unit}> {t(unitKey)}</span>}
                    </span>
                  </div>
                  {hasNextPage && <div style={tableStyles.loadMoreCell(isMobile)} />}
                  {row.values.map((value, idx) => (
                    <div
                      key={block.dates[idx]}
                      style={tableStyles.cell(isMobile, isTextRow, isHighlighted)}
                    >
                      {formatCellValue(value)}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }
)

PondBlock.displayName = 'PondBlock'

// `linear-gradient` of one colour layered on white paints the translucent tint as an
// opaque fill, which is what a sticky cell needs.
const blendOverWhite = (color: string) =>
  `linear-gradient(${color}, ${color}), ${palette.light.grey[0]}`
